import React, { useCallback, useRef, useState } from "react"
import { useHistory } from "react-router-dom"
import {
	Button,
	ButtonGroup,
	Grid,
	Grow,
	MenuItem,
	MenuList,
	Paper,
	Popper
} from "@material-ui/core"
import { Add as AddIcon, ArrowDropDown as ArrowDropDownIcon } from "@material-ui/icons"

import ChatBotTemplatePicker from "@/pages/Admin/Flow/ChatBotCreation/ChatBotTemplate"
import ChatBotGroupPermissionGate from "@/components/PermissionGate/ChatBotGroupPermissionGate"
import { InfoDialog, JsonEditor, Notification } from "@/components"
import ErrorHandlerService from "@/services/ErrorHandler"
import { ErrorType } from "@/hooks/useValidation"
import ChatBotFlowService from "@/services/ChatBotFlow"
import { ShortFlow } from "@/protocols/chatBotConstructor"
import { ChatBotTabType } from "@/pages/Admin/ChatBot/ChatBotList"

type ChatBotCreationProps = {
	chatBotType?: ChatBotTabType
	handleGetChatBotList?: () => Promise<void>
	onChatBotCreated?: () => Promise<void>
}

const ChatBotCreation: React.FC<ChatBotCreationProps> = (props) => {
	const { chatBotType, handleGetChatBotList, onChatBotCreated } = props

	const history = useHistory()
	const anchorRef = useRef<HTMLDivElement>(null)

	const [isDropdownOpen, setIsDropdownOpen] = useState(false)
	const [isPickerOpen, setIsPickerOpen] = useState(false)
	const [importDialog, setImportDialog] = useState<{ open: boolean, loading: boolean }>({
		open: false,
		loading: false
	})

	const isBotCreationDisabled = chatBotType === "bots-v1"

	const redirectToFlow = useCallback((flowId: number) => {
		history.push(`/admin/automation/flow/${flowId}`)
	}, [history])

	const handleImportChatBot = async (chatBotFlow: ShortFlow) => {
		setImportDialog(prev => ({ ...prev, loading: true }))

		try {
			const imported = await ChatBotFlowService.import(chatBotFlow)

			if (imported.type === "flow") {
				history.push(`/admin/automation/flow/${imported.id}`)
			} else {
				history.push(`/admin/bots/${imported.id}`)
			}

			Notification.success({ message: "Fluxo do bot importado com sucesso!" })
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
			Notification.error({ message: "Não conseguimos importar o fluxo desse bot" })
		}

		setImportDialog({ open: false, loading: false })
	}

	return (
		<Grid container alignItems="center" spacing={1} wrap="nowrap">
			<Grid item>
				<ChatBotGroupPermissionGate action="create">
					<ButtonGroup
						variant="contained"
						color="primary"
						ref={anchorRef}
						disabled={isBotCreationDisabled}
					>
						<Button
							startIcon={<AddIcon />}
							onClick={() => setIsPickerOpen(true)}
						>
								Adicionar Bot
						</Button>
						<Button
							size="small"
							onClick={() => setIsDropdownOpen(prev => !prev)}
						>
							<ArrowDropDownIcon />
						</Button>
					</ButtonGroup>
				</ChatBotGroupPermissionGate>

				<Popper
					open={isDropdownOpen}
					anchorEl={anchorRef.current}
					placement="bottom-end"
					transition
					disablePortal
					style={{ zIndex: 1 }}
				>
					{({ TransitionProps }) => (
						<Grow {...TransitionProps} style={{ transformOrigin: "right top" }}>
							<Paper>
								<MenuList>
									<MenuItem
										onClick={() => {
											setIsDropdownOpen(false)
											setImportDialog({ open: true, loading: false })
										}}
									>
											Importar Bot
									</MenuItem>
								</MenuList>
							</Paper>
						</Grow>
					)}
				</Popper>
			</Grid>

			<ChatBotTemplatePicker
				open={isPickerOpen}
				onClose={() => setIsPickerOpen(false)}
				handleGetChatBotList={handleGetChatBotList ?? onChatBotCreated}
				redirectToFlow={redirectToFlow}
			/>

			<InfoDialog
				title="Importar Bot"
				openDialog={importDialog.open}
				onClose={() => setImportDialog({ open: false, loading: false })}
			>
				<Grid container>
					<Grid item xs={12}>
						<JsonEditor
							onSave={async (data) => await handleImportChatBot(data as unknown as ShortFlow)}
							placeholder="Cole aqui o código do Bot que deseja importar!"
							height="300px"
						/>
					</Grid>
				</Grid>
			</InfoDialog>
		</Grid>
	)
}

export default ChatBotCreation
